import type { ChatMessage, ChatStructuredResponse } from '@unreserved/types';

type AmenityLink = {
  type: string;
  search_term?: string;
  label?: string;
  icon?: string;
  url: string;
};

export interface GetAiChatHistoryParams {
  listingId: string;
  userId?: string;
}

export interface ChatHistoryApiResponse {
  messages: Array<{
    message_id: string;
    role: 'user' | 'bot';
    content: string;
    metadata?: {
      answer?: string;
      nearby_properties?: unknown[];
      amenity_links?: AmenityLink[];
    };
    created_at: string;
  }>;
}

export async function getAiChatHistory({
  listingId,
  userId,
}: GetAiChatHistoryParams): Promise<ChatMessage[]> {
  const params = new URLSearchParams({
    listing_id: listingId,
  });

  if (userId) {
    params.append('user_id', userId);
  }

  const apiUrl = process.env.NEXT_PUBLIC_API_URL_AI || process.env.EXPO_PUBLIC_API_URL_AI;
  
  const res = await fetch(
    `${apiUrl}/api/v1/chat/history?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  // 404 means no chat history exists, which is expected - return empty array
  if (res.status === 404) {
    return [];
  }

  if (!res.ok) {
    throw new Error(`Request failed with status ${res.status}`);
  }

  const data = (await res.json()) as ChatHistoryApiResponse;

  // Transform API response to ChatMessage format
  return data.messages.map((msg) => {
    if (msg.role === 'user') {
      return {
        id: msg.message_id,
        role: 'user',
        text: msg.content,
        timestamp: msg.created_at,
      } as ChatMessage;
    } else {
      // Bot/assistant message
      const metadata = msg.metadata || {};
      const response: ChatStructuredResponse = {
        answer: metadata.answer || msg.content,
        nearbyPropertiesJson:
          metadata.nearby_properties as ChatStructuredResponse['nearbyPropertiesJson'],
        amenityLinks: metadata.amenity_links?.map((link: AmenityLink) => ({
          url: link.url,
        })),
      };

      return {
        id: msg.message_id,
        role: 'assistant',
        response,
        timestamp: msg.created_at,
      } as ChatMessage;
    }
  });
}
